import { Button, CheckboxFormField, ClassName, Form, IFormRender, Theme } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import DashboardRibbon from 'components/foundation/dashboard/DashboardRibbon/DashboardRibbon';
import { useToastOpen } from 'contexts/ToastContext';
import { usePositionUpdate } from 'hooks';
import { t } from 'i18next';
import { isEqual } from 'lodash';
import { useRef } from 'react';

export interface ISafetyTrainingsFormProps {
  className?: ClassName;
  positionId: string;
  onSubmit: () => void;
  performTraining: boolean;
  manageTraining: boolean;
}

export interface ISafetyTrainingsFormData {
  performTraining: boolean;
  manageTraining: boolean;
}

const SafetyTrainingsForm = (props: ISafetyTrainingsFormProps) => {
  const { className, positionId, onSubmit, performTraining, manageTraining } = props;

  const [, update] = usePositionUpdate();
  const toast = useToastOpen();
  const formDataRef = useRef<ISafetyTrainingsFormData>({
    performTraining,
    manageTraining,
  });

  const handleFormSubmit = async (formData: ISafetyTrainingsFormData) => {
    if (!isEqual(formDataRef.current, formData)) {
      try {
        await update({
          id: positionId,
          performTraining: formData.performTraining,
          manageTraining: formData.performTraining ? formData.manageTraining : false,
        });
      } catch (error) {
        toast({ label: t('Something went wrong. Please try again later.') });
      }
    }
    onSubmit();
  };

  const performLabel = () => (
    <>
      <h4>{t('Perform Trainings')}</h4>
      <h6>
        {t(
          'Allow the user to be assigned to conduct safety training meetings. They can also create new meetings but can only assign themselves as the instructor.'
        )}
      </h6>
    </>
  );

  const manageLabel = () => (
    <>
      <h4>{t('Manage Trainings')}</h4>
      <h6>
        {t(
          'Allow the user to assign other instructors to lead training meetings. They can also view, edit, and delete meetings created by others.'
        )}
      </h6>
    </>
  );

  const renderFormFields = (form: IFormRender<ISafetyTrainingsFormData>) => (
    <div className={'m-5 pt-8'}>
      <CheckboxFormField name={'performTraining'} form={form} label={performLabel()} />
      {form.state.data.performTraining && (
        <DashboardRibbon theme={Theme.LIGHT}>
          <CheckboxFormField name={'manageTraining'} form={form} label={manageLabel()} />
        </DashboardRibbon>
      )}
      <Button onClick={form.handleSubmit} type="primary" className={'mt-4'}>
        {t('Update')}
      </Button>
    </div>
  );

  const classes = classNames('safety-trainings-form', className);

  return <Form render={renderFormFields} onSubmit={handleFormSubmit} data={formDataRef.current} className={classes} />;
};

export default SafetyTrainingsForm;
